'use client';

import Image from 'next/image';
import React from 'react';
import Link from 'next/link';

import PoweredBySerefin from '../components/shared/PoweredBySerefin';
import AnimatedGradientButton from '../components/shared/AnimatedGradientButton';
import CopyrightNotice from '@/app/components/shared/CopyrightNotice';
import { validateEmail } from '@/lib/validation/email';
import styles from './styles.module.css';

interface FormData {
  username: string;
}

interface FormErrors {
  username?: string;
}

interface FormState {
  form: FormData;
  formErrors: FormErrors;
}

export default function ForgotPassword() {
  const [formState, setFormState] = React.useState<FormState>({
    form: {
      username: '',
    },
    formErrors: {},
  });

  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [isEmailSent, setIsEmailSent] = React.useState<boolean>(false);
  const [errorMessage, setErrorMessage] = React.useState<string | false>(false);

  const form = formState.form;
  const formErrors = formState.formErrors;

  const handleChange = (value: string): void => {
    setFormState(prevState => ({
      ...prevState,
      form: {
        username: value,
      },
    }));
  };

  const validateForm = (): boolean => {
    const stepErrors: FormErrors = {};

    const emailError = validateEmail(form.username);
    if (emailError) stepErrors.username = emailError;

    setFormState(prevState => ({
      ...prevState,
      formErrors: stepErrors,
    }));

    return Object.keys(stepErrors).length === 0;
  };

  const handleSubmit = async (
    e: React.FormEvent<HTMLFormElement>
  ): Promise<void> => {
    e.preventDefault();
    const isValid = validateForm();

    if (isValid) {
      setIsLoading(true);
      setErrorMessage(false);
      try {
        const response = await fetch('/api/auth/forgot-password', {
          body: JSON.stringify({
            username: form.username,
          }),
          headers: {
            'Content-Type': 'application/json',
          },
          method: 'POST',
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || 'Failed to send reset email');
        }

        setIsEmailSent(true);
      } catch (error) {
        setErrorMessage(
          error instanceof Error
            ? error.message
            : 'Unable to send reset email. Please try again.'
        );
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <>
      <div className="flex min-h-screen grow">
        <div className="flex grow items-center justify-center">
          <div className="grid h-full w-full grid-cols-1 md:grid-cols-2">
            {/* Form Section */}
            <div className="flex w-full flex-col p-6 sm:max-w-[600px] sm:justify-self-end md:p-9">
              <Link href="/">
                <Image
                  src="/trustiLogoGradient.svg"
                  className="mb-20"
                  height={38}
                  width={110}
                  alt="Trusti Logo"
                />
              </Link>
              <h1 className={`pageHeading ${styles.heading}`}>
                {isEmailSent ? 'Check Your Email' : 'Forgot Your Password?'}
              </h1>

              {isEmailSent ? (
                <div className="flex flex-col gap-6">
                  <div className="rounded-lg bg-green-50 p-4">
                    <p className="text-sm text-green-700">
                      We've sent a password reset link to your email address.
                      Please check your inbox and follow the instructions to
                      reset your password.
                    </p>
                  </div>

                  <AnimatedGradientButton
                    as="link"
                    href="/sign-in"
                    className="w-full sm:w-auto"
                  >
                    Return to Sign In
                  </AnimatedGradientButton>
                </div>
              ) : (
                <>
                  <p className="mb-6 text-sm text-gray-600">
                    Enter your email address and we'll send you a link to reset
                    your password.
                  </p>

                  {errorMessage && (
                    <div className="mb-6 rounded-lg bg-red-50 p-4">
                      <p className="text-sm text-red-700">{errorMessage}</p>
                    </div>
                  )}

                  <form className="flex flex-col gap-6" onSubmit={handleSubmit}>
                    <div className="flex flex-col gap-2">
                      <label
                        className="text-sm font-medium text-gray-700"
                        htmlFor="email"
                      >
                        Email Address
                      </label>
                      <input
                        type="email"
                        id="email"
                        value={form.username}
                        onChange={e => handleChange(e.target.value)}
                        placeholder="Enter your email"
                        className={`w-full rounded-lg border px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none ${
                          formErrors.username
                            ? 'border-red-500'
                            : 'border-gray-300'
                        }`}
                      />
                      {formErrors.username && (
                        <p className="text-sm text-red-600">
                          {formErrors.username}
                        </p>
                      )}
                    </div>

                    <AnimatedGradientButton
                      as="button"
                      type="submit"
                      disabled={isLoading}
                      className="w-full"
                    >
                      {isLoading ? 'Sending...' : 'Send Reset Link'}
                    </AnimatedGradientButton>
                  </form>

                  <div className="mt-8 text-center">
                    <p className="text-sm text-gray-600">
                      Remember your password?{' '}
                      <Link
                        className="text-blue-600 hover:text-blue-800"
                        href="/sign-in"
                      >
                        Sign in
                      </Link>
                    </p>
                  </div>
                </>
              )}
            </div>

            {/* Decorative Section (Hidden on Mobile) */}
            <div
              className={`${styles.gradientBackground} flex w-full justify-start px-6 pt-6 pb-16 md:p-9`}
            >
              <div className="flex w-full max-w-md flex-col justify-self-end">
                <PoweredBySerefin
                  className="hidden sm:block sm:self-end"
                  isWhite
                />
                <div className={`${styles.signUpContainer} sm:hidden`}>
                  <h3>Remember your password?</h3>
                  <Link href="/sign-in">Sign In.</Link>
                </div>
                <div className={styles.serefinContainer}>
                  <PoweredBySerefin />
                  <CopyrightNotice className="uppercase" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
